// ============================================================================
// CHANGO V5.0 — D4 Ops Admin Seats console
// ============================================================================
// As of Task #49 (owner-role bootstrap):
//   * Grant / revoke are OWNER-ONLY, single-step. No more two-person
//     admin review for seat changes.
//   * Admin users can still VIEW the list (for transparency + audit), but
//     the grant / revoke buttons are disabled for them.
//   * Owner row is listed in a separate "Owner" section above admins —
//     owner cannot be revoked from this UI (it's locked in the server too).
//
// §20 guardrails mirrored here (UI only — server is authoritative):
//   * Self-grant / self-revoke impossible — current user's own row shows
//     "自己账号不可操作" instead of action buttons.
//   * Last-admin lockout — owner can force-revoke the last admin with an
//     explicit opt-in (force=true); UI surfaces this as a secondary button.
//   * Grant target must be an already-verified user ID (the ID picker is a
//     plain numeric input; a proper email→id lookup lands in Phase 2).
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';

const REASON_MIN = 8;
const REASON_MAX = 1000;

function formatTs(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('zh-CN', { hour12: false });
  } catch {
    return iso;
  }
}

function GrantDialog({ open, onClose, onSubmitted }) {
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) { setUserId(''); setReason(''); setBusy(false); setErr(null); }
  }, [open]);

  if (!open) return null;

  const uid = Number.parseInt(userId, 10);
  const uidOk = Number.isInteger(uid) && uid > 0;
  const reasonTrim = reason.trim();
  const reasonOk = reasonTrim.length >= REASON_MIN && reasonTrim.length <= REASON_MAX;
  const formOk = uidOk && reasonOk && !busy;

  const submit = async () => {
    if (!formOk) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.post('/api/ops/admin-seats/grant', {
        user_id: uid, reason: reasonTrim
      });
      onSubmitted?.(res, 'grant');
      onClose();
    } catch (e) {
      setErr(e.message || '提交失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/70" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-base font-semibold text-slate-800">授予 admin 角色</h3>
        <p className="mt-1 text-xs text-slate-500">
          目标账号必须已验证邮箱。由 owner 直接授权，单步生效。
        </p>

        <label className="mt-4 block">
          <span className="text-sm font-medium text-slate-700">目标 user_id</span>
          <input
            type="number"
            min="1"
            step="1"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-chango-500 focus:outline-none"
            placeholder="例如 42"
          />
          {!uidOk && userId !== '' && (
            <span className="mt-1 block text-xs text-rose-600">请输入有效的正整数 user_id</span>
          )}
        </label>

        <label className="mt-4 block">
          <span className="text-sm font-medium text-slate-700">授权理由</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            maxLength={REASON_MAX}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-chango-500 focus:outline-none"
            placeholder="为什么此人需要 admin 权限？"
          />
          <span className="mt-1 flex justify-between text-xs text-slate-400">
            <span>{reasonTrim.length}/{REASON_MAX}</span>
            {reasonTrim.length > 0 && !reasonOk && (
              <span className="text-rose-500">至少 {REASON_MIN} 个字符</span>
            )}
          </span>
        </label>

        {err && (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {err}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >取消</button>
          <button
            type="button"
            onClick={submit}
            disabled={!formOk}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >{busy ? '提交中…' : '提交授予申请'}</button>
        </div>
      </div>
    </div>
  );
}

function RevokeDialog({ open, admin, onClose, onSubmitted }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) { setReason(''); setBusy(false); setErr(null); }
  }, [open, admin?.id]);

  if (!open || !admin) return null;

  const reasonTrim = reason.trim();
  const reasonOk = reasonTrim.length >= REASON_MIN && reasonTrim.length <= REASON_MAX;
  const formOk = reasonOk && !busy;

  const submit = async () => {
    if (!formOk) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.post('/api/ops/admin-seats/revoke', {
        user_id: admin.id, reason: reasonTrim, force: false
      });
      onSubmitted?.(res, 'revoke');
      onClose();
    } catch (e) {
      // LAST_ADMIN_LOCKOUT → surface a "force" button in a follow-up dialog.
      // For now just show the message; owner can retry with ?force=true
      // by re-opening the dialog.
      setErr(e.message || '提交失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/70" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-base font-semibold text-rose-800">撤销 admin 角色</h3>
        <p className="mt-1 text-xs text-slate-500">
          目标：<code className="rounded bg-slate-100 px-1">{admin.email}</code>  (user_id #{admin.id})
        </p>
        <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          由 owner 直接撤销，单步生效。该账号将立即失去 ops 后台所有权限，
          需重新授权才能恢复。
        </p>

        <label className="mt-4 block">
          <span className="text-sm font-medium text-slate-700">撤销理由</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            maxLength={REASON_MAX}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-chango-500 focus:outline-none"
            placeholder="为什么要撤销这名 admin？"
          />
          <span className="mt-1 flex justify-between text-xs text-slate-400">
            <span>{reasonTrim.length}/{REASON_MAX}</span>
            {reasonTrim.length > 0 && !reasonOk && (
              <span className="text-rose-500">至少 {REASON_MIN} 个字符</span>
            )}
          </span>
        </label>

        {err && (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {err}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >取消</button>
          <button
            type="button"
            onClick={submit}
            disabled={!formOk}
            className="rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
          >{busy ? '提交中…' : '提交撤销申请'}</button>
        </div>
      </div>
    </div>
  );
}

function useAdminSeats() {
  const [state, setState] = useState({
    owners: [], admins: [], pending: [], activeCount: 0, loading: true, error: null
  });
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((x) => x + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const res = await api.get('/api/ops/admin-seats');
        if (cancelled) return;
        setState({
          owners: Array.isArray(res?.owners) ? res.owners : [],
          admins: Array.isArray(res?.admins) ? res.admins : [],
          pending: Array.isArray(res?.pending_proposals) ? res.pending_proposals : [],
          activeCount: Number.isInteger(res?.active_admin_count) ? res.active_admin_count : 0,
          loading: false,
          error: null
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof HttpError && err.status === 404) {
          setState({ owners: [], admins: [], pending: [], activeCount: 0, loading: false, error: null });
        } else {
          setState((s) => ({ ...s, loading: false, error: err.message || '加载失败' }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  return { ...state, reload };
}

export default function OpsAdminSeats() {
  const { user } = useAuth();
  const { owners, admins, activeCount, loading, error, reload } = useAdminSeats();
  const [grantOpen, setGrantOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [okMsg, setOkMsg] = useState(null);

  // §ST.10 Plan B: ops_users.role ENUM 只有 support/ops；owner 在 is_owner 字段（platform_ownership join）
  const isOwner = user?.is_owner === true || user?.role === 'owner';
  const lastAdminWarning = activeCount <= 1;

  const onSubmitted = (res, kind) => {
    const suffix = res?.target_user_id ? `user #${res.target_user_id}` : '';
    setOkMsg(`已${kind === 'grant' ? '授予' : '撤销'} admin 角色 ${suffix}`);
    reload();
    setTimeout(() => setOkMsg(null), 6000);
  };

  const activeAdmins = useMemo(
    () => admins.filter((a) => a.disabled_at == null),
    [admins]
  );

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <header className="border-b border-slate-700 bg-slate-800/80">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold">平台管理员席位</h1>
            <p className="text-xs text-slate-400">
              {user?.email} · {user?.role}
              {isOwner
                ? ' · 你以 owner 身份直接授予 / 撤销（单步生效，记入审计）'
                : ' · admin 只能查看；授予 / 撤销仅限 owner'}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={reload}
              className="rounded border border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700"
            >刷新</button>
            <button
              type="button"
              onClick={() => setGrantOpen(true)}
              disabled={!isOwner}
              title={isOwner ? '' : '仅 owner 可授予 admin'}
              className="rounded bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >+ 授予 admin</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        {lastAdminWarning && (
          <div className="mb-4 rounded-md border border-amber-500/50 bg-amber-900/30 px-4 py-2 text-sm text-amber-100">
            ⚠ 当前只有 {activeCount} 名活跃 admin — 撤销按钮已禁用以防 LAST_ADMIN_LOCKOUT。
          </div>
        )}

        {(error || okMsg) && (
          <div className={[
            'mb-4 rounded-md border px-4 py-2 text-sm',
            error
              ? 'border-rose-500/40 bg-rose-900/30 text-rose-200'
              : 'border-emerald-500/40 bg-emerald-900/30 text-emerald-200'
          ].join(' ')}>
            {error || okMsg}
          </div>
        )}

        {owners.length > 0 && (
          <section className="mb-6 overflow-hidden rounded-lg border border-amber-600/50 bg-amber-900/10">
            <div className="border-b border-amber-600/40 px-4 py-3 text-sm font-semibold text-amber-100">
              平台 owner ({owners.length})
            </div>
            <ul className="divide-y divide-amber-600/30 text-sm">
              {owners.map((o) => (
                <li key={o.id} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs">#{o.id}</span>
                      <span>{o.email}</span>
                      <span className="rounded bg-amber-500 px-2 py-0.5 text-xs font-semibold text-amber-950">
                        OWNER
                      </span>
                    </div>
                    <div className="text-xs text-amber-200/70">创建 {formatTs(o.created_at)}</div>
                  </div>
                  <span className="text-xs text-amber-300/70">
                    此席位不可从此处撤销（需直接改库）
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="overflow-hidden rounded-lg border border-slate-700 bg-slate-800/40">
          <div className="border-b border-slate-700/60 px-4 py-3 text-sm font-semibold">
            活跃 admin ({activeAdmins.length} / 总 {admins.length})
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-800/80 text-left text-xs font-medium uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">邮箱</th>
                <th className="px-4 py-3">状态</th>
                <th className="px-4 py-3">创建时间</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">加载中…</td></tr>
              )}
              {!loading && admins.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">暂无 admin</td></tr>
              )}
              {!loading && admins.map((a) => {
                const isSelf = a.id === user?.id;
                const active = a.disabled_at == null;
                return (
                  <tr key={a.id} className="border-t border-slate-700/60">
                    <td className="px-4 py-3 font-mono text-xs">#{a.id}</td>
                    <td className="px-4 py-3">{a.email}</td>
                    <td className="px-4 py-3">
                      <span className={[
                        'rounded px-2 py-0.5 text-xs font-medium',
                        active ? 'bg-emerald-200 text-emerald-900' : 'bg-slate-300 text-slate-700'
                      ].join(' ')}>
                        {active ? '活跃' : '已禁用'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-400">{formatTs(a.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {isSelf ? (
                        <span className="text-xs text-slate-500">自己账号不可操作</span>
                      ) : !isOwner ? (
                        <span className="text-xs text-slate-500">仅 owner 可撤销</span>
                      ) : active ? (
                        <button
                          type="button"
                          onClick={() => setRevokeTarget(a)}
                          className="rounded border border-rose-400/60 px-3 py-1 text-xs text-rose-300 hover:bg-rose-900/30"
                        >撤销</button>
                      ) : (
                        <span className="text-xs text-slate-500">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        <p className="mt-6 text-center text-xs text-slate-500">
          Task #49：owner 单步授予 / 撤销；日常 ops 操作（SKU 定价、工作区归档、退款、扮演）
          仍走 admin 两人复核。所有动作记入 <code className="text-slate-400">ops_action_log</code>。
        </p>
      </main>

      <GrantDialog
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
        onSubmitted={onSubmitted}
      />
      <RevokeDialog
        open={Boolean(revokeTarget)}
        admin={revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onSubmitted={onSubmitted}
      />
    </div>
  );
}
